import raw from '../data/practices.json';
import sequences from '../data/sequences.json';
import durations from '../data/audio-durations.json';
import type { Category, IconName, Practice, Step } from '../types';

interface RawPractice {
  id: string;
  title: string;
  blurb: string;
  category: string;
  icon: string;
  kind: 'sequence' | 'timer' | 'track' | 'free' | 'external';
  sequence?: string;
  rounds?: number;
  roundsAdjustable?: boolean;
  music?: string;
  audio?: string;
  loops?: number;
  minutes?: number;
  note?: string;
  infographic?: string;
  link?: string;
  video?: string;
}

export const audioSrc = (id: string) => `/audio/${id}.m4a`;
export const audioSeconds = (id: string) => (durations as Record<string, number>)[id] ?? 0;

const sequenceByTitle = new Map(sequences.map(s => [s.title, s]));

function build(p: RawPractice): Practice {
  let steps: Step[] = [];

  if (p.kind === 'sequence') {
    const seq = sequenceByTitle.get(p.sequence ?? '');
    if (!seq) throw new Error(`Unknown sequence "${p.sequence}" for practice ${p.id}`);
    steps = seq.asanas.map(a => ({
      kind: 'pose' as const,
      nameEn: a.name_en,
      nameSa: a.name_sanskrit,
      seconds: a.duration_sec,
      repeat: 1,
    }));
  } else if (p.kind === 'track' && p.audio) {
    steps = [{ kind: 'track', src: audioSrc(p.audio), title: p.title, seconds: audioSeconds(p.audio), loops: p.loops ?? 1 }];
  } else if (p.kind === 'timer') {
    steps = [{ kind: 'timer', title: p.title, seconds: (p.minutes ?? 5) * 60 }];
  }

  return {
    id: p.id,
    title: p.title,
    blurb: p.blurb,
    category: p.category as Category,
    icon: p.icon as IconName,
    steps,
    rounds: p.rounds ?? 1,
    roundsAdjustable: p.roundsAdjustable ?? false,
    music: p.music,
    infographic: p.infographic,
    free: p.kind === 'free',
    note: p.note,
    external: p.kind === 'external' ? { link: p.link ?? '', video: p.video, note: 'Opens YouTube Music' } : undefined,
  };
}

export const practices: Practice[] = (raw as RawPractice[]).map(build);

export const getPractice = (id: string) => practices.find(p => p.id === id);

export const categories: { id: Category; label: string }[] = [
  { id: 'asana', label: 'Asana' },
  { id: 'pranayama', label: 'Pranayama' },
  { id: 'chanting', label: 'Chanting' },
  { id: 'meditation', label: 'Meditation and rest' },
  { id: 'music', label: 'Music and movement' },
  { id: 'learning', label: 'Learn' },
];

/** Default minutes for a free/timer practice. */
export const defaultMinutes = (p: Practice): number | null => {
  if (p.free) return 20;
  const timer = p.steps.find(s => s.kind === 'timer');
  return timer ? timer.seconds / 60 : null;
};
